import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Package, ArrowRight, CheckCircle, Loader2, Plus, X, Layers, Trash2, Edit, Box, Settings, PackagePlus, AlertTriangle, Ban, Minus, Undo2, FileDown, Printer, PencilLine, Copy, ChevronDown, BookmarkPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useFuncionarios, FuncionarioSelect } from '@/components/FuncionarioSelect';
import { printItensCliente } from '@/lib/printItensCliente';
import { EditarQuantidadesDialog } from '@/components/EditarQuantidadesDialog';
import { KitPadraoEditor } from '@/components/KitPadraoEditor';

export default function MontagemPage() {
  const [montagensPendentes, setMontagensPendentes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [tiposCaixa, setTiposCaixa] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const { user } = useAuth();
  const isAdmin = ['admin', 'administrador'].includes(user?.perfil || user?.role);
  const navigate = useNavigate();
  const { funcionarios } = useFuncionarios();

  // Funcionário selecionado para montagem
  const [montagemFuncId, setMontagemFuncId] = useState('');
  const [montagemFuncNome, setMontagemFuncNome] = useState('');

  // Box composition state
  const [selectedMontagem, setSelectedMontagem] = useState(null);
  const [isBoxDialogOpen, setIsBoxDialogOpen] = useState(false);
  const [caixas, setCaixas] = useState([]);

  // Tipo caixa management
  const [isTipoCaixaDialogOpen, setIsTipoCaixaDialogOpen] = useState(false);
  const [novoTipoCaixa, setNovoTipoCaixa] = useState({ nome: '', descricao: '' });
  const [editingTipoCaixa, setEditingTipoCaixa] = useState(null);
  const [deletingTipoCaixa, setDeletingTipoCaixa] = useState(null);

  // Reprovação state
  const [reprovados, setReprovados] = useState([]);
  const [reprovarDialog, setReprovarDialog] = useState(null); // { materialId, materialNome }
  const [reprovarQtd, setReprovarQtd] = useState(1);
  const [reprovarMotivo, setReprovarMotivo] = useState('defeito');
  const [editQtdMontagem, setEditQtdMontagem] = useState(null);

  const handleQtdSaved = (data) => {
    if (selectedMontagem && data.montagem?.id === selectedMontagem.id) setSelectedMontagem(data.montagem);
    fetchData();
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [montagensRes, clientesRes, materiaisRes, tiposRes] = await Promise.all([
        api.get('/montagens/pendentes'),
        api.get('/clientes'),
        api.get('/materiais'),
        api.get('/tipos-caixa')
      ]);
      setMontagensPendentes(montagensRes.data);
      setClientes(clientesRes.data);
      setMateriais(materiaisRes.data);
      setTiposCaixa(tiposRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getClienteNome = (id) => clientes.find(c => c.id === id)?.nome || 'N/A';
  const getMaterialNome = (id) => materiais.find(m => m.id === id)?.nome || 'Diverso';
  const getMaterialCodigo = (id) => materiais.find(m => m.id === id)?.codigo || '';

  // Calculate total received quantities from recepcao items
  const totalQtds = useMemo(() => {
    if (!selectedMontagem) return {};
    const qtds = {};
    for (const item of (selectedMontagem.itens || [])) {
      const matId = item.material_id;
      if (matId) qtds[matId] = (qtds[matId] || 0) + (item.quantidade || 1);
    }
    return qtds;
  }, [selectedMontagem]);

  // Calculate reprovado quantities
  const reprovadoQtds = useMemo(() => {
    const qtds = {};
    for (const rep of reprovados) {
      const matId = rep.material_id;
      if (matId) qtds[matId] = (qtds[matId] || 0) + (rep.quantidade || 0);
    }
    return qtds;
  }, [reprovados]);

  // Available = total - reprovados
  const availableQtds = useMemo(() => {
    const qtds = {};
    for (const [matId, total] of Object.entries(totalQtds)) {
      qtds[matId] = total - (reprovadoQtds[matId] || 0);
    }
    return qtds;
  }, [totalQtds, reprovadoQtds]);

  // Calculate used quantities across all boxes
  const usedQtds = useMemo(() => {
    const used = {};
    for (const caixa of caixas) {
      for (const item of (caixa.itens || [])) {
        const matId = item.material_id;
        if (matId) used[matId] = (used[matId] || 0) + (item.quantidade || 1);
      }
    }
    return used;
  }, [caixas]);

  const getRemainingQtd = (matId) => (availableQtds[matId] || 0) - (usedQtds[matId] || 0);

  // Open box composition dialog
  const handleOpenBoxDialog = (montagem) => {
    if (!montagem.itens || montagem.itens.length === 0) {
      toast.error('Esta montagem não possui materiais recebidos. Verifique a recepção.');
      return;
    }
    setSelectedMontagem(montagem);
    setCaixas(montagem.caixas || []);
    setReprovados(montagem.reprovados || []);
    setIsBoxDialogOpen(true);
  };

  const [scrollToBoxId, setScrollToBoxId] = useState(null);
  useEffect(() => {
    if (!scrollToBoxId) return;
    const t = setTimeout(() => {
      document.querySelector(`[data-box-id="${scrollToBoxId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setScrollToBoxId(null);
    }, 80);
    return () => clearTimeout(t);
  }, [scrollToBoxId]);

  // Add a new empty box
  const handleAddBox = (tipoCaixaId) => {
    const tipo = tiposCaixa.find(t => t.id === tipoCaixaId);
    if (!tipo) return;
    const newId = `cx-${Date.now()}`;
    const itens = [];
    const faltas = [];
    for (const c of tipo.composicao || []) {
      const q = Math.min(c.quantidade, Math.max(0, getRemainingQtd(c.material_id)));
      if (q > 0) itens.push({ material_id: c.material_id, quantidade: q });
      if (q < c.quantidade) faltas.push(`${getMaterialNome(c.material_id)} (${c.quantidade - q})`);
    }
    setScrollToBoxId(newId);
    setCaixas(prev => [...prev, {
      id: newId,
      tipo_caixa_id: tipoCaixaId,
      tipo_caixa_nome: tipo.nome,
      itens,
      _open: true
    }]);
    if (faltas.length) toast.warning(`Kit padrão incompleto: faltam ${faltas.join(', ')}`);
    else if (itens.length) toast.success(`"${tipo.nome}" preenchida com o kit padrão`);
  };

  // Save a composed box as the default kit of its box type
  const handleSavePadrao = async (boxIndex) => {
    const cx = caixas[boxIndex];
    const composicao = cx.itens.filter(i => i.quantidade > 0).map(i => ({ material_id: i.material_id, quantidade: i.quantidade }));
    if (!window.confirm(`Salvar esta composição como kit padrão de "${cx.tipo_caixa_nome}"?\n\nNas próximas montagens essa caixa já virá preenchida.`)) return;
    try {
      const res = await api.put(`/tipos-caixa/${cx.tipo_caixa_id}/composicao`, { composicao });
      setTiposCaixa(prev => prev.map(t => t.id === cx.tipo_caixa_id ? { ...t, composicao: res.data.composicao } : t));
      toast.success(`Kit padrão de "${cx.tipo_caixa_nome}" salvo`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar kit padrão');
    }
  };

  // Remove a box
  const handleRemoveBox = (boxIndex) => {
    setCaixas(prev => prev.filter((_, i) => i !== boxIndex));
  };

  // How many identical copies of a box still fit in the remaining stock
  const getMaxCopies = (boxIndex) => {
    const itens = (caixas[boxIndex]?.itens || []).filter(i => i.quantidade > 0);
    if (itens.length === 0) return 0;
    return Math.min(...itens.map(i => Math.floor(getRemainingQtd(i.material_id) / i.quantidade)));
  };

  // Duplicate a composed box N times
  const handleReplicateBox = (boxIndex, copies) => {
    const max = getMaxCopies(boxIndex);
    if (copies < 1) return;
    if (copies > max) { toast.error(`Materiais suficientes para no máximo ${max} cópia(s)`); return; }
    const base = caixas[boxIndex];
    const novas = Array.from({ length: copies }, (_, n) => ({
      ...base,
      id: `cx-${Date.now()}-${n}`,
      _open: false,
      itens: base.itens.filter(i => i.quantidade > 0).map(i => ({ ...i }))
    }));
    setCaixas(prev => [...prev.slice(0, boxIndex + 1), ...novas, ...prev.slice(boxIndex + 1)]);
    toast.success(`${copies} cópia(s) de "${base.tipo_caixa_nome}" criada(s)`);
  };

  // Add material to a specific box
  const handleAddMaterialToBox = (boxIndex, materialId, quantidade) => {
    const remaining = getRemainingQtd(materialId);
    if (quantidade > remaining) {
      toast.error(`Quantidade máxima disponível: ${remaining}`);
      return;
    }
    setCaixas(prev => {
      const updated = [...prev];
      const existing = updated[boxIndex].itens.findIndex(i => i.material_id === materialId);
      if (existing >= 0) {
        updated[boxIndex].itens[existing].quantidade += quantidade;
      } else {
        updated[boxIndex].itens = [...updated[boxIndex].itens, { material_id: materialId, quantidade }];
      }
      return updated;
    });
  };

  // Remove material from a box
  const handleRemoveMaterialFromBox = (boxIndex, itemIndex) => {
    setCaixas(prev => {
      const updated = [...prev];
      updated[boxIndex].itens = updated[boxIndex].itens.filter((_, i) => i !== itemIndex);
      return updated;
    });
  };

  // Set exact quantity for a material in a specific box
  const handleSetMaterialQtyInBox = (boxIndex, materialId, newQty) => {
    setCaixas(prev => prev.map((caixa, i) => {
      if (i !== boxIndex) return caixa;
      const existingIdx = caixa.itens.findIndex(it => it.material_id === materialId);
      let newItens;
      if (newQty <= 0) {
        newItens = caixa.itens.filter(it => it.material_id !== materialId);
      } else if (existingIdx >= 0) {
        newItens = caixa.itens.map((it, idx) => idx === existingIdx ? { ...it, quantidade: newQty } : it);
      } else {
        newItens = [...caixa.itens, { material_id: materialId, quantidade: newQty }];
      }
      return { ...caixa, itens: newItens };
    }));
  };

  // Get qty of a material in a specific box
  const getQtyInBox = (boxIndex, materialId) => {
    const caixa = caixas[boxIndex];
    if (!caixa) return 0;
    const item = caixa.itens.find(it => it.material_id === materialId);
    return item ? item.quantidade : 0;
  };

  // Get remaining global qty available for a material (excluding what's in OTHER boxes, not the current one)
  const getRemainingForBox = (boxIndex, materialId) => {
    const total = availableQtds[materialId] || 0;
    let usedInOtherBoxes = 0;
    caixas.forEach((caixa, i) => {
      if (i === boxIndex) return;
      const item = caixa.itens.find(it => it.material_id === materialId);
      if (item) usedInOtherBoxes += item.quantidade;
    });
    return total - usedInOtherBoxes;
  };

  // Reprovar material
  const handleReprovarMaterial = async () => {
    if (!selectedMontagem || !reprovarDialog) return;
    if (reprovarQtd <= 0) { toast.error('Quantidade inválida'); return; }
    try {
      const res = await api.post(`/montagens/${selectedMontagem.id}/reprovar-material`, {
        material_id: reprovarDialog.materialId,
        quantidade: reprovarQtd,
        motivo: reprovarMotivo
      });
      toast.success(res.data.message);
      const updated = res.data.montagem;
      setReprovados(updated.reprovados || []);
      setSelectedMontagem(updated);
      setReprovarDialog(null);
      setReprovarQtd(1);
      setReprovarMotivo('defeito');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reprovar material');
    }
  };

  // Desfazer reprovação
  const handleDesfazerReprovacao = async (reprovadoId) => {
    if (!selectedMontagem) return;
    try {
      const res = await api.delete(`/montagens/${selectedMontagem.id}/reprovar-material/${reprovadoId}`);
      toast.success(res.data.message);
      const updated = res.data.montagem;
      setReprovados(updated.reprovados || []);
      setSelectedMontagem(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desfazer reprovação');
    }
  };

  // Gerar PDF de reprovados
  const handleGerarPDFReprovados = async () => {
    if (!selectedMontagem) return;
    try {
      const response = await api.get(`/montagens/${selectedMontagem.id}/pdf-reprovados`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success('PDF de reprovados gerado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar PDF');
    }
  };

  // Save all boxes
  const handleSaveBoxes = async () => {
    if (!selectedMontagem) return;
    if (caixas.length === 0) {
      toast.error('Adicione pelo menos uma caixa');
      return;
    }
    if (!montagemFuncId) {
      toast.error('Selecione o funcionário responsável pela montagem');
      return;
    }
    for (const [i, caixa] of caixas.entries()) {
      if (caixa.itens.length === 0) {
        toast.error(`Caixa "${caixa.tipo_caixa_nome}" (#${i + 1}) está vazia. Adicione materiais.`);
        return;
      }
    }
    setProcessing(true);
    try {
      await api.post(`/montagens/${selectedMontagem.id}/salvar-caixas`, {
        caixas: caixas.map(({ _open, ...c }) => c),
        funcionario_id: montagemFuncId,
        funcionario_nome: montagemFuncNome
      });
      toast.success(`${caixas.length} caixa(s) salva(s) com sucesso!`);
      setIsBoxDialogOpen(false);
      setMontagemFuncId('');
      setMontagemFuncNome('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar caixas');
    } finally {
      setProcessing(false);
    }
  };

  // Send to ciclo
  const handleEnviarParaCiclo = (montagemId) => {
    navigate(`/ciclos?montagem=${montagemId}`);
    toast.info('Redirecionando para criar ciclo...');
  };

  // Create tipo caixa
  const handleCreateTipoCaixa = async () => {
    if (!novoTipoCaixa.nome.trim()) { toast.error('Informe o nome da caixa'); return; }
    try {
      if (editingTipoCaixa) {
        await api.put(`/tipos-caixa/${editingTipoCaixa.id}`, novoTipoCaixa);
        toast.success('Tipo de caixa atualizado!');
        setEditingTipoCaixa(null);
      } else {
        await api.post('/tipos-caixa', novoTipoCaixa);
        toast.success('Tipo de caixa cadastrado!');
      }
      setNovoTipoCaixa({ nome: '', descricao: '' });
      const res = await api.get('/tipos-caixa');
      setTiposCaixa(res.data);
    } catch (error) {
      toast.error('Erro ao salvar tipo de caixa');
    }
  };

  const handleEditTipoCaixa = (tipo) => {
    setEditingTipoCaixa(tipo);
    setNovoTipoCaixa({ nome: tipo.nome, descricao: tipo.descricao || '', composicao: tipo.composicao || [] });
    setIsTipoCaixaDialogOpen(true);
  };

  const handleDeleteTipoCaixa = async (tipoId) => {
    try {
      await api.delete(`/tipos-caixa/${tipoId}`);
      toast.success('Tipo de caixa excluído');
      setDeletingTipoCaixa(null);
      const res = await api.get('/tipos-caixa');
      setTiposCaixa(res.data);
    } catch (error) {
      toast.error('Erro ao excluir tipo de caixa');
    }
  };

  const handleDeleteMontagem = async (montagemId) => {
    const m = montagensPendentes.find(x => x.id === montagemId);
    const nome = m?.cliente_nome || getClienteNome(m?.cliente_id);
    if (!window.confirm(`ATENÇÃO: EXCLUIR a montagem ${m?.numero_montagem || ''} do cliente ${nome}?\n\nAs caixas montadas serão perdidas e a recepção volta para a tela de Recepção.\n\nPara salvar as caixas, cancele e use o botão verde "Salvar Caixa(s)".`)) return;
    try {
      const res = await api.delete(`/montagens/${montagemId}`);
      toast.success(res.data?.message || 'Montagem excluída');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir montagem');
    }
  };

  const handlePrintItens = (montagem) => {
    const ok = printItensCliente({
      montagem,
      clienteNome: montagem.cliente_nome || getClienteNome(montagem.cliente_id),
      getMaterialNome,
      getMaterialCodigo
    });
    if (!ok) toast.error('Permita pop-ups no navegador para imprimir');
  };

  const materiaisOptions = materiais.map(m => ({
    id: m.id,
    label: m.nome,
    sublabel: `Código: ${m.codigo}`
  }));

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="montagem-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Montagem / Composição de Caixas
          </h1>
          <p className="text-lg text-slate-600 mt-2">Monte caixas cirúrgicas com os materiais recebidos</p>
        </div>
        <Button onClick={() => setIsTipoCaixaDialogOpen(true)} variant="outline" data-testid="btn-cadastrar-tipo-caixa">
          <Settings className="w-4 h-4 mr-2" />
          Cadastrar Tipo de Caixa
        </Button>
      </div>

      {/* Montagens Pendentes */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200 bg-amber-50">
          <h2 className="text-lg font-semibold text-amber-800 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Montagens Aguardando Composição ({montagensPendentes.filter(m => !m.caixas || m.caixas.length === 0).length})
          </h2>
        </div>
        {montagensPendentes.filter(m => !m.caixas || m.caixas.length === 0).length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>Nenhuma montagem aguardando composição de caixas</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {montagensPendentes.filter(m => !m.caixas || m.caixas.length === 0).map((montagem) => (
              <div key={montagem.id} className="p-4 hover:bg-slate-50 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-mono bg-amber-100 text-amber-800 px-2 py-1 rounded">{montagem.numero_montagem}</span>
                    <span className="font-medium text-slate-900">{montagem.cliente_nome || getClienteNome(montagem.cliente_id)}</span>
                  </div>
                  <div className="mt-2 text-sm text-slate-600">
                    {(!montagem.itens || montagem.itens.length === 0) ? (
                      <span className="text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Nenhum material recebido — verifique a recepção
                      </span>
                    ) : (
                      <>
                        <span className="mr-4">📦 {montagem.itens?.length || 0} material(is) recebido(s)</span>
                        {montagem.itens?.map((item, idx) => (
                          <span key={idx} className="inline-block text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded mr-1 mt-1">
                            {getMaterialNome(item.material_id)} x{item.quantidade}
                          </span>
                        ))}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handlePrintItens(montagem)} disabled={!montagem.itens || montagem.itens.length === 0} data-testid={`btn-imprimir-itens-${montagem.id}`}>
                    <Printer className="w-4 h-4 mr-2" /> Imprimir Itens
                  </Button>
                  <Button variant="outline" className="text-blue-600" onClick={() => setEditQtdMontagem(montagem)} disabled={!montagem.recepcao_id || !montagem.itens?.length} data-testid={`btn-editar-qtd-${montagem.id}`}>
                    <PencilLine className="w-4 h-4 mr-2" /> Editar Quantidade
                  </Button>
                  <Button onClick={() => handleOpenBoxDialog(montagem)} className="bg-amber-600 hover:bg-amber-700" disabled={!montagem.itens || montagem.itens.length === 0} data-testid={`btn-compor-caixas-${montagem.id}`}>
                    <Layers className="w-4 h-4 mr-2" />
                    Compor Caixas
                  </Button>
                  {isAdmin && (
                    <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteMontagem(montagem.id)} data-testid={`btn-excluir-montagem-${montagem.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Montagens Prontas */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200 bg-green-50">
          <h2 className="text-lg font-semibold text-green-800 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Prontas para Esterilização ({montagensPendentes.filter(m => m.caixas && m.caixas.length > 0).length})
          </h2>
        </div>
        {montagensPendentes.filter(m => m.caixas && m.caixas.length > 0).length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Layers className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>Nenhuma montagem pronta para ciclo</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {montagensPendentes.filter(m => m.caixas && m.caixas.length > 0).map((montagem) => (
              <div key={montagem.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-mono bg-green-100 text-green-800 px-2 py-1 rounded">{montagem.numero_montagem}</span>
                    <span className="font-medium text-slate-900">{montagem.cliente_nome || getClienteNome(montagem.cliente_id)}</span>
                    <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">{montagem.caixas?.length} caixa(s)</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handlePrintItens(montagem)} data-testid={`btn-imprimir-itens-pronta-${montagem.id}`}>
                      <Printer className="w-4 h-4 mr-1" /> Imprimir Itens
                    </Button>
                    <Button variant="outline" size="sm" className="text-blue-600" onClick={() => setEditQtdMontagem(montagem)} disabled={!montagem.recepcao_id} data-testid={`btn-editar-qtd-pronta-${montagem.id}`}>
                      <PencilLine className="w-4 h-4 mr-1" /> Editar Quantidade
                    </Button>
                    <Button onClick={() => handleOpenBoxDialog(montagem)} variant="outline" size="sm">
                      <Edit className="w-4 h-4 mr-1" /> Editar
                    </Button>
                    <Button onClick={() => handleEnviarParaCiclo(montagem.id)} className="bg-green-600 hover:bg-green-700" data-testid={`btn-enviar-ciclo-${montagem.id}`}>
                      <ArrowRight className="w-4 h-4 mr-2" /> Enviar para Ciclo
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteMontagem(montagem.id)} data-testid={`btn-excluir-montagem-${montagem.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {/* Preview boxes */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {montagem.caixas?.map((caixa, idx) => (
                    <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-1">
                        <Box className="w-3.5 h-3.5 text-blue-600" />
                        {caixa.tipo_caixa_nome}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {caixa.itens?.length || 0} material(is) | {caixa.itens?.reduce((s, i) => s + (i.quantidade || 1), 0) || 0} peças
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog: Compor Caixas */}
      <Dialog open={isBoxDialogOpen} onOpenChange={setIsBoxDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => {
          if (e.target.closest('#searchable-select-portal') || document.getElementById('searchable-select-portal')?.contains(e.target)) {
            e.preventDefault();
          }
        }} onInteractOutside={(e) => {
          if (document.getElementById('searchable-select-portal')) {
            e.preventDefault();
          }
        }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-amber-600" />
              Composição de Caixas Cirúrgicas
            </DialogTitle>
          </DialogHeader>
          
          {selectedMontagem && (
            <div className="space-y-4">
              {/* Info */}
              <div className="bg-slate-50 p-3 rounded-lg flex items-center justify-between">
                <div>
                  <span className="text-sm text-slate-500">Montagem:</span>
                  <span className="ml-2 font-medium">{selectedMontagem.numero_montagem}</span>
                  <span className="ml-4 text-sm text-slate-500">Cliente:</span>
                  <span className="ml-2 font-medium">{selectedMontagem.cliente_nome || getClienteNome(selectedMontagem.cliente_id)}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="text-blue-600" onClick={() => setEditQtdMontagem(selectedMontagem)} disabled={!selectedMontagem.recepcao_id} data-testid="btn-editar-qtd-dialog">
                    <PencilLine className="w-4 h-4 mr-1" /> Editar Quantidade
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handlePrintItens(selectedMontagem)} data-testid="btn-imprimir-itens-dialog">
                    <Printer className="w-4 h-4 mr-1" /> Imprimir Itens
                  </Button>
                </div>
              </div>

              {/* Funcionário Responsável */}
              <div className="border border-blue-200 bg-blue-50 rounded-lg p-3">
                <FuncionarioSelect
                  value={montagemFuncId}
                  onChange={(id, nome) => { setMontagemFuncId(id); setMontagemFuncNome(nome); }}
                  funcionarios={funcionarios}
                />
              </div>

              {/* Painel fixo: saldo de materiais + adicionar caixa */}
              <div className="sticky -top-4 sm:-top-6 z-20 -mx-4 px-4 sm:-mx-6 sm:px-6 pt-3 pb-3 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm space-y-2" data-testid="painel-fixo-materiais">
              {/* Available materials balance */}
              <div className="border border-slate-200 rounded-lg p-2.5 bg-white">
                <Label className="text-sm font-semibold text-slate-700">Materiais Disponíveis (Recepção)</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                  {Object.entries(totalQtds).map(([matId, totalQtd]) => {
                    const reprovado = reprovadoQtds[matId] || 0;
                    const disponivel = availableQtds[matId] || 0;
                    const usado = usedQtds[matId] || 0;
                    const remaining = disponivel - usado;
                    return (
                      <div key={matId} className={`text-xs px-2.5 py-1.5 rounded-lg border flex items-center gap-1.5 ${remaining === 0 && disponivel > 0 ? 'bg-green-50 border-green-200 text-green-700' : reprovado > 0 ? 'bg-white border-slate-200' : 'bg-white border-slate-200'}`}>
                        <span className="font-medium">{getMaterialNome(matId)}</span>
                        <span className="text-slate-500">
                          {usado}/{disponivel}
                          {reprovado > 0 && <span className="text-red-500 ml-1">({reprovado} repr.)</span>}
                          {remaining === 0 && disponivel > 0 && ' ✓'}
                        </span>
                        {remaining > 0 && (
                          <button
                            type="button"
                            className="ml-1 text-red-400 hover:text-red-600 transition-colors"
                            title="Reprovar material"
                            onClick={() => { setReprovarDialog({ materialId: matId, materialNome: getMaterialNome(matId) }); setReprovarQtd(1); }}
                            data-testid={`btn-reprovar-${matId}`}
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Add Box button */}
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <SearchableSelect
                    options={tiposCaixa.map(t => ({ id: t.id, label: t.nome, sublabel: t.descricao || '' }))}
                    value=""
                    onChange={(tipoCaixaId) => handleAddBox(tipoCaixaId)}
                    placeholder="Selecione o tipo de caixa para adicionar..."
                    searchPlaceholder="Buscar tipo de caixa..."
                    emptyMessage="Nenhum tipo cadastrado"
                  />
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setIsTipoCaixaDialogOpen(true)} title="Cadastrar novo tipo">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              </div>

              {/* Reprovados section */}
              {reprovados.length > 0 && (
                <div className="border border-red-200 rounded-lg p-3 bg-red-50/50">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
                      <Ban className="w-4 h-4" />
                      Materiais Reprovados ({reprovados.length})
                    </Label>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      onClick={() => handleGerarPDFReprovados()}
                      data-testid="btn-pdf-reprovados"
                    >
                      <FileDown className="w-3.5 h-3.5 mr-1.5" />
                      Gerar PDF
                    </Button>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {reprovados.map((rep) => (
                      <div key={rep.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-red-100" data-testid={`reprovado-${rep.id}`}>
                        <div className="flex-1">
                          <span className="text-sm font-medium text-red-800">{getMaterialNome(rep.material_id)}</span>
                          <span className="text-sm text-red-600 ml-2">x{rep.quantidade}</span>
                          <span className="text-xs text-red-400 ml-2 bg-red-50 px-1.5 py-0.5 rounded">
                            {rep.motivo === 'defeito' ? 'Defeito' : rep.motivo === 'vencido' ? 'Vencido' : rep.motivo === 'contaminado' ? 'Contaminado' : rep.motivo === 'danificado' ? 'Danificado' : rep.motivo}
                          </span>
                          {rep.responsavel_nome && <span className="text-xs text-slate-400 ml-2">por {rep.responsavel_nome}</span>}
                        </div>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-400 hover:text-blue-600" onClick={() => handleDesfazerReprovacao(rep.id)} title="Desfazer reprovação" data-testid={`btn-desfazer-${rep.id}`}>
                          <Undo2 className="w-3.5 h-3.5 mr-1" />
                          <span className="text-xs">Desfazer</span>
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Boxes list */}
              {caixas.length === 0 ? (
                <div className="p-8 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
                  <PackagePlus className="w-10 h-10 mx-auto mb-2" />
                  <p>Selecione um tipo de caixa acima para começar</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {caixas.map((caixa, boxIdx) => (
                    <BoxComposer
                      key={caixa.id}
                      caixa={caixa}
                      boxIndex={boxIdx}
                      allMaterials={Object.keys(availableQtds).map(matId => ({
                        id: matId,
                        nome: getMaterialNome(matId),
                        codigo: getMaterialCodigo(matId)
                      }))}
                      getQtyInBox={getQtyInBox}
                      getRemainingForBox={getRemainingForBox}
                      onSetQty={handleSetMaterialQtyInBox}
                      onRemoveBox={handleRemoveBox}
                      maxCopies={getMaxCopies(boxIdx)}
                      onReplicate={handleReplicateBox}
                      padrao={tiposCaixa.find(t => t.id === caixa.tipo_caixa_id)?.composicao || []}
                      getMaterialNome={getMaterialNome}
                      onSavePadrao={handleSavePadrao}
                    />
                  ))}
                </div>
              )}

              {/* Save */}
              <div className="flex gap-2 justify-end pt-2 border-t">
                <Button variant="outline" onClick={() => setIsBoxDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleSaveBoxes} disabled={processing || caixas.length === 0} className="bg-green-600 hover:bg-green-700" data-testid="btn-salvar-caixas">
                  {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  {processing ? 'Salvando...' : `Salvar ${caixas.length} Caixa(s)`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Cadastrar Tipo de Caixa */}
      <Dialog open={isTipoCaixaDialogOpen} onOpenChange={(open) => { setIsTipoCaixaDialogOpen(open); if (!open) { setEditingTipoCaixa(null); setNovoTipoCaixa({ nome: '', descricao: '' }); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTipoCaixa ? 'Editar Tipo de Caixa' : 'Gerenciar Tipos de Caixa'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome da Caixa</Label>
              <Input value={novoTipoCaixa.nome} onChange={e => setNovoTipoCaixa(p => ({ ...p, nome: e.target.value }))} placeholder="Ex: Caixa Ortopédica" className="mt-1" data-testid="input-tipo-caixa-nome" />
            </div>
            <div>
              <Label>Descrição (opcional)</Label>
              <Input value={novoTipoCaixa.descricao} onChange={e => setNovoTipoCaixa(p => ({ ...p, descricao: e.target.value }))} placeholder="Descrição..." className="mt-1" />
            </div>
            <KitPadraoEditor
              composicao={novoTipoCaixa.composicao || []}
              onChange={(composicao) => setNovoTipoCaixa(p => ({ ...p, composicao }))}
              materiaisOptions={materiaisOptions}
              getMaterialNome={getMaterialNome}
            />
            <div className="flex justify-end gap-2">
              {editingTipoCaixa && (
                <Button variant="outline" onClick={() => { setEditingTipoCaixa(null); setNovoTipoCaixa({ nome: '', descricao: '' }); }}>
                  Cancelar Edição
                </Button>
              )}
              <Button onClick={handleCreateTipoCaixa} data-testid="btn-salvar-tipo-caixa">
                <Plus className="w-4 h-4 mr-2" /> {editingTipoCaixa ? 'Atualizar' : 'Cadastrar'}
              </Button>
            </div>
            {tiposCaixa.length > 0 && (
              <div className="border-t pt-3">
                <Label className="text-xs text-slate-500 mb-2 block">Tipos cadastrados ({tiposCaixa.length}):</Label>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {tiposCaixa.map(t => (
                    <div key={t.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 group">
                      <div>
                        <span className="text-sm font-medium text-slate-800">{t.nome}</span>
                        {t.descricao && <span className="text-xs text-slate-500 ml-2">— {t.descricao}</span>}
                        {t.composicao?.length > 0 && <span className="text-[11px] text-emerald-700 ml-2" data-testid={`tipo-kit-padrao-${t.id}`}>· kit padrão: {t.composicao.reduce((s, c) => s + c.quantidade, 0)} pç</span>}
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-500 hover:text-blue-700" onClick={() => handleEditTipoCaixa(t)} data-testid={`btn-edit-tipo-${t.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeleteTipoCaixa(t.id)} data-testid={`btn-delete-tipo-${t.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Reprovar Material */}
      <Dialog open={!!reprovarDialog} onOpenChange={(open) => { if (!open) setReprovarDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Ban className="w-5 h-5" />
              Reprovar Material
            </DialogTitle>
          </DialogHeader>
          {reprovarDialog && (
            <div className="space-y-4">
              <div className="bg-red-50 rounded-lg p-3 text-sm">
                <span className="font-medium text-red-800">{reprovarDialog.materialNome}</span>
                <p className="text-red-600 text-xs mt-1">Informe a quantidade com defeito e o motivo da reprovação</p>
              </div>
              <div>
                <Label>Quantidade a reprovar</Label>
                <Input
                  type="number" min="1" value={reprovarQtd}
                  onChange={e => setReprovarQtd(Math.max(1, parseInt(e.target.value) || 1))}
                  onFocus={e => e.target.select()}
                  className="mt-1"
                  data-testid="input-reprovar-qtd"
                />
              </div>
              <div>
                <Label>Motivo</Label>
                <select
                  value={reprovarMotivo}
                  onChange={e => setReprovarMotivo(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  data-testid="select-reprovar-motivo"
                >
                  <option value="defeito">Defeito</option>
                  <option value="danificado">Danificado</option>
                  <option value="vencido">Vencido</option>
                  <option value="contaminado">Contaminado</option>
                  <option value="oxidado">Oxidado</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setReprovarDialog(null)}>Cancelar</Button>
                <Button onClick={handleReprovarMaterial} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirmar-reprovar">
                  <Ban className="w-4 h-4 mr-2" />
                  Reprovar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EditarQuantidadesDialog
        open={!!editQtdMontagem}
        onClose={() => setEditQtdMontagem(null)}
        titulo={editQtdMontagem ? `${editQtdMontagem.numero_montagem} — ${editQtdMontagem.cliente_nome || getClienteNome(editQtdMontagem.cliente_id)}` : ''}
        itens={editQtdMontagem?.itens}
        endpoint={`/montagens/${editQtdMontagem?.id}/quantidades`}
        getMaterialNome={getMaterialNome}
        onSaved={handleQtdSaved}
      />
    </div>
  );
}

// Box Composer Component — lista todos materiais com botões +/−
function BoxComposer({ caixa, boxIndex, allMaterials, getQtyInBox, getRemainingForBox, onSetQty, onRemoveBox, maxCopies, onReplicate, padrao = [], getMaterialNome, onSavePadrao }) {
  const [copies, setCopies] = useState(1);
  const [open, setOpen] = useState(!!caixa._open);
  const totalPecas = caixa.itens.reduce((s, i) => s + (i.quantidade || 0), 0);
  const totalItens = caixa.itens.filter(i => i.quantidade > 0).length;
  const faltasPadrao = padrao
    .map(p => ({ ...p, falta: p.quantidade - (caixa.itens.find(i => i.material_id === p.material_id)?.quantidade || 0) }))
    .filter(p => p.falta > 0);

  return (
    <div className="border border-blue-200 rounded-lg overflow-hidden scroll-mt-60" data-testid={`box-${boxIndex}`} data-box-id={caixa.id}>
      {/* Box header */}
      <div className="bg-blue-50 px-4 py-2.5 flex items-center justify-between gap-2">
        <button type="button" className="flex items-center gap-2 text-left min-w-0 flex-1" onClick={() => setOpen(o => !o)} title={open ? 'Fechar caixa' : 'Abrir caixa'} data-testid={`btn-toggle-box-${boxIndex}`}>
          <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${open ? '' : '-rotate-90'}`} />
          <Box className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-blue-900 tabular-nums w-9 shrink-0">#{boxIndex + 1}</span>
          <span className="font-medium text-blue-900 truncate">{caixa.tipo_caixa_nome}</span>
          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded whitespace-nowrap shrink-0">{totalItens} itens | {totalPecas} pç</span>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <div className={`flex items-center gap-1 border rounded-md px-2 py-1 ${totalItens > 0 ? 'bg-white border-blue-300' : 'bg-slate-50 border-slate-200'}`} title={totalItens > 0 ? 'Criar caixas iguais a esta' : 'Coloque os materiais deste kit primeiro'}>
            <Copy className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-medium text-slate-700">Mais kits iguais:</span>
            <Input type="number" min="1" max={maxCopies || 1} value={copies} onChange={e => setCopies(Math.max(1, parseInt(e.target.value) || 1))} onFocus={e => e.target.select()} className="h-7 w-16 text-center text-sm" disabled={totalItens === 0 || maxCopies < 1} data-testid={`input-replicar-${boxIndex}`} />
            <span className="text-xs text-slate-400 w-16 text-center whitespace-nowrap" data-testid={`max-replicar-${boxIndex}`}>{totalItens > 0 ? `máx ${maxCopies}` : 'monte 1 kit'}</span>
            <Button size="sm" className="h-7 px-2 bg-blue-600 hover:bg-blue-700" disabled={totalItens === 0 || maxCopies < 1 || copies > maxCopies} onClick={() => { onReplicate(boxIndex, copies); setCopies(1); setOpen(false); }} data-testid={`btn-replicar-${boxIndex}`}>
              Criar
            </Button>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onRemoveBox(boxIndex)} className="text-red-500 hover:text-red-700 h-7 w-7 p-0" data-testid={`btn-remove-box-${boxIndex}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {!open && (
        <button type="button" className="w-full text-left px-4 py-2 text-xs text-slate-600 hover:bg-slate-50" onClick={() => setOpen(true)} data-testid={`box-summary-${boxIndex}`}>
          {totalItens > 0
            ? caixa.itens.filter(i => i.quantidade > 0).map(i => `${allMaterials.find(m => m.id === i.material_id)?.nome || 'Material'} x${i.quantidade}`).join(' · ')
            : 'Caixa vazia — clique para abrir e adicionar materiais'}
        </button>
      )}

      {faltasPadrao.length > 0 && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-start gap-2" data-testid={`box-faltas-${boxIndex}`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span><b>Abaixo do kit padrão:</b> {faltasPadrao.map(f => `${getMaterialNome(f.material_id)} (faltam ${f.falta})`).join(' · ')}</span>
        </div>
      )}

      {/* Materials list with +/- controls */}
      {open && <div className="divide-y divide-slate-100">
        {allMaterials.map((mat) => {
          const qtyInBox = getQtyInBox(boxIndex, mat.id);
          const maxAvailable = getRemainingForBox(boxIndex, mat.id);
          const isAdded = qtyInBox > 0;

          return (
            <div key={mat.id} className={`px-4 py-2 flex items-center justify-between ${isAdded ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`} data-testid={`mat-row-${boxIndex}-${mat.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {mat.codigo && <span className="text-xs text-slate-400 font-mono">{mat.codigo}</span>}
                  <span className={`text-sm truncate ${isAdded ? 'font-medium text-slate-900' : 'text-slate-600'}`}>{mat.nome}</span>
                </div>
                <span className="text-xs text-slate-400">Disponível: {maxAvailable}</span>
              </div>
              <div className="flex items-center gap-1 ml-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-full"
                  disabled={qtyInBox <= 0}
                  onClick={() => onSetQty(boxIndex, mat.id, qtyInBox - 1)}
                  data-testid={`btn-minus-${boxIndex}-${mat.id}`}
                >
                  <span className="text-lg leading-none">−</span>
                </Button>
                <input
                  type="number"
                  min="0"
                  max={maxAvailable}
                  value={qtyInBox}
                  onFocus={e => e.target.select()}
                  onChange={e => {
                    const v = Math.max(0, parseInt(e.target.value) || 0);
                    onSetQty(boxIndex, mat.id, Math.min(v, maxAvailable));
                  }}
                  className={`w-14 h-8 text-center text-sm font-semibold tabular-nums border rounded-md focus:outline-none focus:border-blue-500 ${isAdded ? 'text-blue-700 border-blue-300 bg-white' : 'text-slate-400 border-slate-200'}`}
                  data-testid={`qty-display-${boxIndex}-${mat.id}`}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-full"
                  disabled={qtyInBox >= maxAvailable}
                  onClick={() => onSetQty(boxIndex, mat.id, qtyInBox + 1)}
                  data-testid={`btn-plus-${boxIndex}-${mat.id}`}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>}

      {open && totalItens > 0 && (
        <div className="px-4 py-2 border-t border-slate-100 flex justify-end">
          <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-700 hover:bg-emerald-50" onClick={() => onSavePadrao(boxIndex)} data-testid={`btn-salvar-padrao-${boxIndex}`}>
            <BookmarkPlus className="w-3.5 h-3.5 mr-1" /> Salvar como kit padrão de "{caixa.tipo_caixa_nome}"
          </Button>
        </div>
      )}

      {open && totalItens === 0 && (
        <div className="px-4 py-2 text-center text-xs text-slate-400 border-t border-slate-100">
          Use os botões + ou digite a quantidade de cada material. Depois use "Mais kits iguais" para criar as outras caixas de uma vez.
        </div>
      )}
    </div>
  );
}
